import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash2, Plus, Pencil, Check, X, Truck, ExternalLink, RefreshCw, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const CARRIERS = [
  { slug: "ups", name: "UPS", url: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}` },
  { slug: "fedex", name: "FedEx", url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}` },
  { slug: "usps", name: "USPS", url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}` },
  { slug: "dhl", name: "DHL", url: (n) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encodeURIComponent(n)}` },
  { slug: "amazon", name: "Amazon Logistics", url: (n) => `https://track.amazon.com/tracking/${encodeURIComponent(n)}` },
  { slug: "ontrac", name: "OnTrac", url: (n) => `https://www.ontrac.com/tracking/?number=${encodeURIComponent(n)}` },
  { slug: "other", name: "Other", url: () => "" },
];
const carrierMeta = (slug) => CARRIERS.find((c) => c.slug === slug) || CARRIERS.find((c) => c.slug === "other");

const STATUS_TONE = {
  Delivered: "#506B52", "Out for delivery": "#8C7154", "In transit": "#5C6B8C",
  Exception: "#9E4747", "Delivery attempt failed": "#9E4747",
};
const statusTone = (label) => STATUS_TONE[label] || "#5C5651";

const EMPTY = { item: "", vendor: "", carrier_slug: "", tracking_number: "", estimated_delivery: "", status_label: "", notes: "" };

export default function OrdersTab({ projectId }) {
  const [orders, setOrders] = useState([]);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = () =>
    api.get(`/projects/${projectId}/orders`).then((r) => {
      setOrders(r.data.orders || []);
      setTrackingEnabled(!!r.data.tracking_enabled);
    }).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const c = carrierMeta(form.carrier_slug);
      await api.post(`/projects/${projectId}/orders`, { ...form, carrier_name: c.slug === "other" ? "" : c.name });
      setForm(EMPTY); setShowForm(false); load();
      toast.success("Order added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditForm({
      item: o.item || "", vendor: o.vendor || "", carrier_slug: o.carrier_slug || "",
      tracking_number: o.tracking_number || "", estimated_delivery: o.estimated_delivery || "",
      status_label: o.status_label || "", notes: o.notes || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(EMPTY); };

  const saveEdit = async (id) => {
    try {
      const c = carrierMeta(editForm.carrier_slug);
      await api.put(`/orders/${id}`, { ...editForm, carrier_name: c.slug === "other" ? "" : c.name });
      cancelEdit(); load();
      toast.success("Order updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/orders/${id}`); load(); };

  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      const { data } = await api.post(`/orders/${id}/refresh`);
      if (data.enabled === false) {
        toast.info("Auto-tracking isn't turned on yet. Use the Track button for live carrier status, or update status via Edit.");
      } else {
        toast.success("Status updated from carrier");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't refresh status");
    } finally { setRefreshingId(null); }
  };

  const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const inputCls = "rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]";

  const carrierSelect = (val, onChange, testid) => (
    <Select value={val || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-10 ${inputCls}`}><SelectValue placeholder="Carrier" /></SelectTrigger>
      <SelectContent className="rounded-none">
        {CARRIERS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="max-w-4xl space-y-6" data-testid="orders-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Shipments</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Orders &amp; Tracking</h3>
          <p className="text-sm text-[#5C5651] mt-1">
            Enter the carrier and tracking number — we build a live tracking link.
            {trackingEnabled
              ? " Status and delivery dates auto-update when you open or refresh an order."
              : " Tap Refresh (or Track) to check status."}
          </p>
        </div>
        <Button data-testid="new-order-btn" onClick={() => { setShowForm((s) => !s); setForm(EMPTY); }}
          className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs shrink-0">
          <Plus size={15} className="mr-1" /> New Order
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#EBE5E0] bg-[#F9F8F6] p-4 space-y-3" data-testid="order-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="order-item-input" placeholder="Item / description" required value={form.item}
              onChange={(e) => setForm({ ...form, item: e.target.value })} className={inputCls} />
            <Input data-testid="order-vendor-input" placeholder="Vendor" value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {carrierSelect(form.carrier_slug, (v) => setForm({ ...form, carrier_slug: v }), "order-carrier-select")}
            <Input data-testid="order-tracking-input" placeholder="Tracking number" value={form.tracking_number}
              onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className={inputCls} />
            <Input data-testid="order-eta-input" type="date" placeholder="Expected delivery" value={form.estimated_delivery}
              onChange={(e) => setForm({ ...form, estimated_delivery: e.target.value })} className={inputCls} />
          </div>
          <Input data-testid="order-notes-input" placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)}
              className="rounded-none text-[#5C5651] uppercase tracking-wider text-xs">Cancel</Button>
            <Button data-testid="save-order-btn" type="submit"
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-6">Add Order</Button>
          </div>
        </form>
      )}

      {orders.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center" data-testid="orders-empty">
          <Truck size={28} strokeWidth={1.25} className="mx-auto text-[#A68A6B] mb-3" />
          <p className="text-[#5C5651]">No orders yet. Add one to start tracking shipments.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const c = carrierMeta(o.carrier_slug);
            const trackUrl = o.tracking_number && c.slug !== "other" ? c.url(o.tracking_number) : "";
            const isEditing = editingId === o.id;
            return (
              <div key={o.id} className="border border-[#EBE5E0] bg-white p-5" data-testid={`order-card-${o.id}`}>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input data-testid={`edit-order-item-${o.id}`} value={editForm.item}
                        onChange={(e) => setEditForm({ ...editForm, item: e.target.value })} className={inputCls} placeholder="Item" />
                      <Input data-testid={`edit-order-vendor-${o.id}`} value={editForm.vendor}
                        onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} className={inputCls} placeholder="Vendor" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {carrierSelect(editForm.carrier_slug, (v) => setEditForm({ ...editForm, carrier_slug: v }), `edit-order-carrier-${o.id}`)}
                      <Input data-testid={`edit-order-tracking-${o.id}`} value={editForm.tracking_number}
                        onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })} className={inputCls} placeholder="Tracking number" />
                      <Input data-testid={`edit-order-eta-${o.id}`} type="date" value={editForm.estimated_delivery}
                        onChange={(e) => setEditForm({ ...editForm, estimated_delivery: e.target.value })} className={inputCls} />
                    </div>
                    <Input data-testid={`edit-order-status-${o.id}`} value={editForm.status_label}
                      onChange={(e) => setEditForm({ ...editForm, status_label: e.target.value })} className={inputCls} placeholder="Status (manual)" />
                    <Input data-testid={`edit-order-notes-${o.id}`} value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className={inputCls} placeholder="Notes" />
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`cancel-order-${o.id}`} type="button" variant="ghost" onClick={cancelEdit}
                        className="rounded-none text-[#5C5651] uppercase tracking-wider text-xs"><X size={14} className="mr-1" />Cancel</Button>
                      <Button data-testid={`save-order-edit-${o.id}`} type="button" onClick={() => saveEdit(o.id)}
                        className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-5"><Check size={14} className="mr-1" />Save</Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-serif text-xl text-[#2B2622]">{o.item}</p>
                        <p className="text-xs text-[#9b938b] mt-0.5">
                          {[o.vendor, c.slug !== "other" ? c.name : (o.carrier_name || "Carrier")].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid={`edit-order-${o.id}`} onClick={() => startEdit(o)}
                          className="text-[#5C5651] hover:text-[#A68A6B]"><Pencil size={15} strokeWidth={1.5} /></button>
                        <button data-testid={`delete-order-${o.id}`} onClick={() => remove(o.id)}
                          className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm">
                      <span className="inline-flex items-center gap-2">
                        {o.status_label === "Delivered"
                          ? <PackageCheck size={16} className="text-[#506B52]" />
                          : <Truck size={16} style={{ color: statusTone(o.status_label) }} />}
                        <span data-testid={`order-status-${o.id}`} className="uppercase tracking-wider text-xs font-medium" style={{ color: statusTone(o.status_label) }}>
                          {o.status_label || "No status yet"}
                        </span>
                      </span>
                      {o.tracking_number && (
                        <span className="text-[#5C5651]">Tracking: <span className="text-[#2B2622] font-mono text-xs">{o.tracking_number}</span></span>
                      )}
                      {o.estimated_delivery && (
                        <span className="text-[#5C5651]">Expected: <span className="text-[#2B2622]">{fmtDate(o.estimated_delivery)}</span></span>
                      )}
                      {o.delivered_at && (
                        <span className="text-[#506B52]">Delivered: {fmtDate(o.delivered_at)}</span>
                      )}
                    </div>

                    {o.last_checkpoint_message && (
                      <p className="text-xs text-[#9b938b] mt-2">
                        {o.last_checkpoint_message}{o.last_checkpoint_time ? ` · ${fmtDate(o.last_checkpoint_time)}` : ""}
                      </p>
                    )}
                    {o.notes && <p className="text-sm text-[#5C5651] mt-2 italic">{o.notes}</p>}

                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-[#F2EFEB]">
                      {trackUrl && (
                        <a data-testid={`track-order-${o.id}`} href={trackUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#8C7154] hover:bg-[#A68A6B] hover:text-white px-3 py-2 text-xs uppercase tracking-wider transition-colors">
                          <ExternalLink size={13} /> Track shipment
                        </a>
                      )}
                      <button data-testid={`refresh-order-${o.id}`} onClick={() => refresh(o.id)} disabled={refreshingId === o.id}
                        className="inline-flex items-center gap-1.5 border border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B] px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-50">
                        <RefreshCw size={13} className={refreshingId === o.id ? "animate-spin" : ""} /> Refresh status
                      </button>
                      {o.tracking_synced_at && (
                        <span className="text-[10px] uppercase tracking-wider text-[#9b938b]">Synced {fmtDate(o.tracking_synced_at)}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
